import { useEffect, useState } from "react";
import { api, errText } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Star, Check, Music2, Tv, BarChart3, Briefcase, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BENEFITS = [
  [Star, "Gold Star profile badge"], [Music2, "Upload music to VStar Music"], [Tv, "Upload long-form TV programming"],
  [Briefcase, "Business & professional profiles"], [BarChart3, "Advanced analytics & scheduled content"], [Sparkles, "Enhanced creator & livestream tools"],
  [Check, "50 VC withdrawal threshold (vs 100)"], [Check, "Higher upload limits & customization"],
];

export default function Plus() {
  const { user } = useAuth();
  const [interval, setInterval] = useState("monthly");
  const [pricing, setPricing] = useState({ monthly: 14.99, yearly: 149 });
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/subscription").then(({ data }) => { setPricing(data.pricing); setSub(data.subscription); }); }, []);

  const checkout = async () => {
    setBusy(true);
    try { const { data } = await api.post("/payments/checkout", { plan: interval, origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch (e) { toast.error(errText(e)); setBusy(false); }
  };

  const isPlus = user?.plan === "plus";
  return (
    <div className="px-4 md:px-6 py-8 max-w-3xl mx-auto">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full glass glass-gold text-gold text-xs mb-3"><Star size={13} className="fill-[var(--gold)]" /> VIRTUSTARS+</div>
        <h1 className="font-display font-extrabold text-3xl md:text-4xl">Turn your avatar into a <span className="text-gold-grad">business.</span></h1>
        <p className="text-muted-foreground mt-2">Unlock uploads, analytics, lower withdrawal thresholds and pro tools.</p>
      </div>

      {isPlus ? (
        <div className="surface rounded-2xl p-8 text-center glass-gold">
          <Star size={40} className="mx-auto text-gold fill-[var(--gold)] mb-3" />
          <div className="font-display font-bold text-xl">You're a VirtuStars+ member</div>
          <p className="text-sm text-muted-foreground mt-1">Enjoy all premium creator tools.</p>
          {sub?.locked_price != null && (
            <div className="mt-4 inline-flex flex-col items-center gap-0.5 surface-2 rounded-xl px-5 py-3" data-testid="plus-locked-price">
              <span className="text-xs text-muted-foreground uppercase tracking-wide">Your locked-in rate</span>
              <span className="font-mono text-2xl text-gold">${Number(sub.locked_price).toFixed(2)}<span className="text-sm text-muted-foreground">/{sub.interval === "yearly" ? "yr" : "mo"}</span></span>
              <span className="text-[11px] text-muted-foreground">Price protected — future increases won't affect you.</span>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="flex justify-center mb-6">
            <div className="surface-2 rounded-full p-1 flex">
              <button onClick={() => setInterval("monthly")} className={`px-5 py-2 rounded-full text-sm ${interval === "monthly" ? "bg-white text-black" : "text-muted-foreground"}`} data-testid="plus-monthly">Monthly</button>
              <button onClick={() => setInterval("yearly")} className={`px-5 py-2 rounded-full text-sm ${interval === "yearly" ? "bg-white text-black" : "text-muted-foreground"}`} data-testid="plus-yearly">Yearly <span className="text-gold text-xs">save 17%</span></button>
            </div>
          </div>
          <div className="surface rounded-2xl p-8 glass-gold text-center mb-5">
            <div className="font-mono text-5xl text-gold">${interval === "monthly" ? pricing.monthly : pricing.yearly}</div>
            <div className="text-sm text-muted-foreground mt-1">per {interval === "monthly" ? "month" : "year"}</div>
            <Button onClick={checkout} disabled={busy} className="btn-gold w-full max-w-xs mx-auto h-12 rounded-xl mt-6" data-testid="plus-checkout-btn">
              {busy ? <><Loader2 size={18} className="animate-spin mr-2" />Redirecting…</> : <><Star size={18} className="mr-1.5" />Upgrade to VirtuStars+</>}
            </Button>
            <p className="text-xs text-muted-foreground mt-3">Secure checkout via Stripe. Cancel anytime.</p>
          </div>
        </>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        {BENEFITS.map(([Icon, text], i) => (
          <div key={i} className="flex items-center gap-3 surface rounded-xl p-4"><div className="w-9 h-9 rounded-lg bg-gold/15 flex items-center justify-center shrink-0"><Icon size={16} className="text-gold" /></div><span className="text-sm">{text}</span></div>
        ))}
      </div>
    </div>
  );
}
